import { GiftCardWithPoints } from '../../pages/dashboard/GiftCards/GiftCards';
import { BuildingType } from '../DatePicker/DatePicker';
import type { PayRentTransactionDetailPayload } from './payRentTransactionDetailPayload';

export enum TenantPaymentOptionStatusEnum {
  Active = 'active',
  Inactive = 'inactive',
  VerificationPending = 'verificationPending',
  VerificationFailed = 'verificationFailed',
  Expired = 'expired',
  Blocked = 'blocked',
}

/** Whether the payment option can be used for payment (only Active is allowed). */
export function isPaymentOptionActive(status?: string | null): boolean {
  return status === TenantPaymentOptionStatusEnum.Active;
}

/** User-facing label for payment option status. */
export function getPaymentOptionStatusLabel(status?: string | null): string {
  if (!status) return '';
  return status
    .replace(/([A-Z])/g, ' $1')
    .replace(/^./, (str) => str.toUpperCase())
    .trim();
}

/** Short message explaining why the payment option cannot be used (for non-Active statuses). */
export function getPaymentOptionStatusMessage(status?: string | null): string {
  switch (status) {
    case TenantPaymentOptionStatusEnum.Inactive:
      return 'This payment method is inactive. Please select another or set it as active.';
    case TenantPaymentOptionStatusEnum.VerificationPending:
      return 'Verification is pending. Please complete verification to use this payment method.';
    case TenantPaymentOptionStatusEnum.VerificationFailed:
      return 'Verification failed. Please verify again or use a different payment method.';
    case TenantPaymentOptionStatusEnum.Expired:
      return 'This payment method has expired. Please update or add a new one.';
    case TenantPaymentOptionStatusEnum.Blocked:
      return 'This payment method is blocked. Please use a different payment method.';
    default:
      return 'This payment method cannot be used. Please select another or update it.';
  }
}

export type ModalState = {
  /** Rent History row when opening `transaction-detail` from Pay Rent (timeline UI). */
  payRentHistoryTransaction?: PayRentTransactionDetailPayload;
  payNumber?: string;
  /** Called after an e-voucher is redeemed so the opener can refresh rent credit data. */
  onEVoucherRedeemed?: () => void;
  buildingId?: number | null;
  paymentDates?: Record<number, { nextPaymentDate: string; isPaymentDone: boolean }>;
  propertyAmount?: number;
  errorDescription?: string;
  editCard?: PaymentOption;
  tenantBuildingId?: string;
  /**
   * When set, the MyHomes lease-edit UI will highlight and scroll into view
   * the requested lease fields (used for deep links from automated emails).
   */
  leaseEditFocus?: LeaseEditFocus;
  transactionDetails?: {
    cardType: string;
    cardCompany: string;
    transactionDate: string;
    amountPaid: string | number;
    rentAmount: string | number;
    processingFee: string | number;
    fixedFee: string | number;
    status: string;
    percentageRate: number | string;
    percentageProcessingFee: string | number;
    timestampAdded?: string;
    errorMessages?: string | null;
    formattedErrorMessage?: string | null;
  };
  isChangeRequest?: boolean;
  message?: string;
  disableRNT?: boolean;
  phoneNumber?: string;
  isPhoneVerification?: boolean;
  isEmailVerification?: boolean;
  mfaId?: string;
  tenantTransactionId?: string;
  paymentType?: string;
  discountAmount?: string;
  brandCode?: string;
  discountOption?: RentCreditOption | null;
  idvStatus?: string;
  tenantMitSubscriptionId?: string;
  /** After cancel-subscription success, navigate to Pay Rent with fresh flow state. */
  redirectToPayRent?: boolean;
  giftPrices?: number[];
  rentAmount?: number;
  selectedCard?: GiftCardWithPoints | null;
  openTab?: string;
  /** Rewards modal section: `points` | `wallet`. */
  openSection?: string;
  fromModal?: string;
  processDate?: string; // Added for PaymentStatus
  tenantPaymentOptionId?: string; // Added for PaymentStatus to get processDate
  /** Points shown to the user before payment; success UI must not use live balance. */
  earnedPoints?: number;
  refreshProperties?: boolean; // Added to refresh properties after update
  refreshProfile?: boolean; // flag to refresh profile progress status (percentage)
  phoneSettingsMode?: 'add' | 'edit' | 'verify';
  profileProgressData?: tenantProgressRespone | null;
  equifaxSubscribedBuildings?: EquifaxSubscribedBuilding[]; // Added for Equifax subscribed buildings
  refreshEquifaxEligibleBuildings?: boolean; // Added to refresh Equifax eligible buildings
  refreshPaymentOptions?: boolean;
  newCard?: {
    paymentMethod: string;
    cardBrand: string;
    cardCategoryName: string;
    isPremiumCard: boolean;
    percentageRate: string;
  };
  refetchPaymentSummary?: boolean;
  refreshTransactions?: boolean;
  padTermsContent?: string;
  /** First payment placed an auth hold pending IDV completion. */
  pendingIdvAuthHold?: boolean;
  /** Show the post-payment referral prompt: 2nd+ successful payment and IDV verified. */
  showReferralPrompt?: boolean;
  /** Dashboard pay-rent setup when the tenant has no building yet. */
  payRentSetupFlow?: boolean;
  /** Collect rental address and occupation before identity verification. */
  postIdvDetailsFlow?: boolean;
  /** Adding a new building (not completing an existing incomplete one). */
  isNewBuilding?: boolean;
  /** Open pay-rent setup flow after the terms modal is accepted. */
  pendingPayRentSetupAfterTerms?: boolean;
  /** Direct Pay vs Autopay selection from Add Payment Method modal; synced back to Pay Rent. */
  feePaymentPlan?: 'direct' | 'autopay';
  /** Prefill property/address fields in add-home after RNT lookup. */
  propertyPrefill?: PropertyPrefill;
  currentTenantPaymentOptionId?: string;
  onUpdated?: () => void;
  /** Admin note shown in the IDV change-request modal. */
  messageFromAdmin?: string | null;
  /** Whether a new IDV session is required after application changes are submitted. */
  isNewIdvRequired?: boolean;
};

export type LeaseEditFocus = {
  rentAmount?: boolean;
  leaseStartDate?: boolean;
  leaseEndDate?: boolean;
  address?: boolean;
};

export type PropertyPrefill = {
  rent?: number | null;
  address?: string;
  unitNo?: string;
  street?: string;
  house?: string;
  city?: string;
  province?: string;
  provinceCode?: string;
  postal?: string;
  country?: string;
};
export enum NodeEnvEnum {
  Local = 'local',
  Staging = 'staging',
  Production = 'production',
  Test = 'test',
}
export const fadeInUpAnimation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 1, ease: 'easeInOut' },
};
export const slideInAnimation = (margin = -100) => {
  return {
    initial: { opacity: 0, y: 150 },
    whileInView: { opacity: 1, y: 0 },
    transition: { duration: 0.5 },
    viewport: { margin: `${margin}px`, once: true },
  };
};
export interface AddBuildingRequest {
  buildingDetails?: {
    payNumber?: string;
    unitNumber?: string;
    buildingName?: string;
    rentAmount?: number;
    dueDate?: number;
    leaseStartDate?: string;
    leaseEndDate?: string;
    leaseDocumentUrl?: string;
    type?: BuildingType;
  };
  addressDetails?: {
    streetName?: string;
    city?: string;
    province?: string;
    country?: string;
    countryCode?: string;
    postalCode?: string;
    fullAddress?: string;
  };
}
export interface UpdateBuildingRequest {
  buildingDetails: {
    payNumber: string | null;
    unitNumber: string;
    buildingName: string;
    rentAmount: number;
    dueDate: number;
    leaseStartDate: string | null;
    leaseEndDate: string | null;
    leaseDocumentUrl: string | null;
    type?: BuildingType;
  };
  addressDetails: {
    streetName: string;
    city: string;
    province: string;
    country: string;
    countryCode: string;
    postalCode: string;
    fullAddress: string;
    provinceCode?: string;
  };
  managerDetails?: {
    email: string;
    phoneNumber: string;
    firstName: string;
    lastName: string;
    isIndividual: boolean;
    securityQuestion: string;
    securityAnswer: string;
  } | null;
}
export interface rntUpdateRequest {
  buildingDetails: {
    payNumber: string;
  };
}

export interface UpdateLeaseDocument {
  buildingDetails: {
    leaseDocumentUrl: string;
  };
}
export interface managerUpdateRequest {
  /** Sent as `{ payNumber: null }` when switching a building from a TenantPay
   * account number to landlord details, so only one of the two remains. */
  buildingDetails?: {
    payNumber: null;
  };
  managerDetails: {
    email: string;
    phoneNumber: string;
    firstName: string;
    lastName: string;
    phoneNumberExtension: string;
    isIndividual: boolean;
    securityQuestion: string;
    securityAnswer: string;
    memo?: string;
  };
}
export interface rentAddressRequest {
  buildingDetails: {
    rentAmount: number;
    dueDate: number;
  };
  addressDetails: {
    streetName: string;
    city: string;
    province: string;
    country: string;
    countryCode: string;
    postalCode: string;
    fullAddress: string;
    unitNumber: string;
    buildingNumber: string;
    provinceCode?: string;
  };
}
export interface rentDetailsRequest {
  buildingDetails: {
    rentAmount: number;
    dueDate: number;
  };
}
export interface addressDetailsRequest {
  addressDetails: {
    streetName: string;
    city: string;
    province: string;
    country: string;
    countryCode: string;
    postalCode: string;
    fullAddress: string;
    unitNumber: string;
    buildingNumber: string;
    provinceCode?: string;
  };
}
export interface AddBuildingManager {
  buildingDetails: {
    unitNumber: string;
    buildingName: string;
    rentAmount: number;
    dueDate: number;
    leaseStartDate: string;
    leaseEndDate: string;
    leaseDocumentUrl: string;
    type?: BuildingType;
  };
  managerDetails: {
    email: string;
    phoneNumber: string;
    phoneNumberExtension: string;
    firstName: string;
    lastName: string;
    isIndividual: boolean;
    securityQuestion: string;
    securityAnswer: string;
  };
  addressDetails: {
    streetName: string;
    city: string;
    province: string;
    country: string;
    countryCode: string;
    postalCode: string;
    fullAddress: string;
  };
}
export interface PersonalInfoRequest {
  userDetails?: {
    firstName?: string;
    lastName?: string;
    middleName?: string;
    dateOfBirth?: string;
  };
  tenantDetails?: {
    occupation?: string;
    industry?: string;
  };
  addressDetails?: {
    streetName?: string;
    city?: string;
    unitNumber?: string;
    province?: string;
    country?: string;
    countryCode?: string;
    postalCode?: string;
    fullAddress?: string;
    provinceCode?: string;
  };
}
export type ApiResponse = {
  message: string;
  errorMessage?: string;
  timestamp: string;
};

export interface MessageInterface {
  message: string;
}

/** GET /newsletters — subscription status for the current user */
export interface NewsletterSubscriptionApiResponse {
  message: string;
  data?: {
    subscribedOn?: string | null;
  };
}

export type IDVSessionResponse = {
  message: string;
  data: {
    webEnrolmentUrl: string;
    sessionId: string;
    returnUrl: string;
  };
};
export type tenantDetailsResponse = {
  message: string;
  errorMessage?: string;
  timestamp: string;
  data: tenantDetails;
};
export type tenantDetails = {
  user: {
    userId: string;
    middleName: string;
    firstName: string;
    lastName: string;
    phoneNumberExtension: string;
    phoneNumber: string;
    profilePicturePath: string;
    email: string;
    accessToken: string | undefined;
    isEmailVerified: number;
    isPhoneVerified: number;
    dateOfBirth: string;
    profilePictureUrl: string;
    acquisitionSource: string | null;
  };
  tenant: {
    tenantId: string;
    isIdentityVerified: number;
    isNewProperty: number;
    tenantCompanyId: string | null;
    accountType: string;
    occupation: string;
    industry: string;
    tenantIdv: {
      tenantIdvId: number;
      idvId: string;
      messageFromAdmin: string;
      status: string;
      isNewIdvRequired: boolean;
      timestampCompleted?: string;
    };
  };
  address: {
    addressId: string;
    unitNumber: string;
    buildingNumber: string;
    streetName: string;
    city: string;
    province: string;
    country: string;
    countryCode: string;
    postalCode: string;
    fullAddress: string;
    provinceCode: string | null;
  };
  tenantBuildings: {
    tenantBuildingId: string;
  }[];
};
export type Country = {
  icon: string;
  name: string;
  countryCode: string;
};

export interface TenantPaymentOption {
  tenantPaymentOptionId: string;
  isDefault: number;
  paymentMethod: {
    paymentMethodId: string;
    name: string;
  };
  cardCompanyType: {
    cardCompanyTypeId: string;
    value: string;
  };
  cardCategory: {
    cardCategoryId: string;
    name: string;
  };
  tenantBankAccount: TenantBankAccount | null;
}

export type buildingDetailsResponse = {
  message: string;
  data: buildingDetails[];
};
export type buildingDetails = {
  building: {
    tenantBuildingId: string;
    userId: string;
    payNumber: string;
    unitNumber: string;
    buildingName: string;
    addressId: string;
    rentAmount: number;
    dueDateTypeName: string;
    dueDate: number;
    leaseStartDate: string | null;
    leaseEndDate: string | null;
    leaseDocumentUrl: string | null;
    leaseDocumentDownloadUrl: string | null;
    timestampLeaseUploaded: string | null;
    paymentDates: string[];
    addedByUserId: string;
    updatedByUserId: string;
    removedByUserId: string | null;
    removed: number;
    timestampAdded: string;
    timestampUpdated: string;
    timestampRemoved: string | null;
    nextBillingDate: string | null;
    nextDueDate: string;
    nextPaymentDate: string;
    isInitialPaymentRequired: boolean;
    hasTenantMitSubscription: boolean;
    isActive: boolean;
    type?: BuildingType;
  };
  address: {
    addressId: string;
    hashId: string | null;
    unitNumber: string | null;
    buildingNumber: string | null;
    streetName: string;
    city: string;
    province: string;
    country: string;
    countryCode: string;
    postalCode: string;
    fullAddress: string;
    updatedByUserId: string;
    addedByUserId: string;
    timestampAdded: string;
    timestampUpdated: string;
    provinceCode?: string;
  };
  tenantManager?: {
    tenantManagerId: number;
    firstName: string;
    lastName: string;
    email: string;
    phoneNumber: string;
    isIndividual: boolean;
    securityQuestion: string;
    securityAnswer: string;
    memo: string;
  };
  tenantMitSubscription?: {
    tenantMitSubscriptionId: string;
    tenantPaymentOption: TenantPaymentOption | null;
    tenantPaymentOptionId?: string;
  };
  dueDateType?: {
    dueDateTypeId: string;
    name: string;
  };
};
export type IDVresponse = {
  message: string;
  data: {
    sessionId: string;
    status: string | null;
    tenantIdvStatus: string;
    isNewIdvRequired: boolean;
  };
};
export type rentResponse = {
  message: string;
  data: {
    totalRentPaid: number | null;
  };
};

export type Transactions = {
  cardType: string;
  cardCompany: string;
  transactionDate: string;
  amountPaid: string | number;
  rentAmount: string | number;
  processingFee: string | number;
  fixedFee: string | number;
  status: string;
  percentageRate: number | string;
  percentageProcessingFee: string | number;
  timestampAdded: string;
  errorMessages?: string | null;
  formattedErrorMessage?: string | null;
};
export type transactionsResponse = {
  message: string;
  data: [
    {
      tenantTransactionId: string;
      tenantBuildingId: string;
      payNumber: string;
      email: string;
      paymentMethod: string | null;
      paymentType: string;
      amountPaid: number;
      currency: string;
      status: string;
      cardCompany: string | null;
      cardType: string | null;
      timestampAdded: string;
      buildingName: string;
      errorMessages?: string | null;
      formattedErrorMessage?: string | null;
    },
  ];
  meta: {
    page: number;
    limit: number;
    totalCount: number;
    lastPage: number;
    hasPreviousPage: boolean;
    hasNextPage: boolean;
  };
};
export type particularTransaction = {
  tenantTransactionId: string;
  tenantBuildingId: string;
  payNumber: string;
  email: string;
  paymentMethod: string | null;
  paymentType: string;
  transactionType?: TransactionType;
  amountPaid: number;
  currency: string;
  status: string;
  cardCompany: string | null;
  cardType: string | null;
  timestampAdded: string;
  rentAmount?: number;
  processingFee?: number;
  fixedProcessingFee?: number;
  percentageRate?: number;
  percentageProcessingFee?: number;
  buildingName?: string;
  discountAmount?: number;
  paymentMethodName?: string;
  promoCodeDiscountAmount?: string;
  refundAmount?: number;
  tenantPaymentOptionId?: string;
  errorMessages?: string | null;
  formattedErrorMessage?: string | null;
  expectedPaymentSendDate?: string | null;
};
export type singleIndustry = {
  kycBusinessTypeId: string;
  value: string;
};
export type kycIndustryResponse = singleIndustry[];
declare global {
  interface AddressComponent {
    long_name: string;
    short_name: string;
    types: string[];
  }

  interface Place {
    address_components?: AddressComponent[];
    formatted_address: string;
  }

  interface ComponentRestrictions {
    country?: string | string[];
  }

  interface Autocomplete {
    addListener: (event: string, callback: () => void) => void;
    getPlace: () => Place;
  }

  interface GoogleMaps {
    places: {
      Autocomplete: new (
        input: HTMLInputElement,
        options: { types: string[]; componentRestrictions?: ComponentRestrictions }
      ) => Autocomplete;
    };
  }
  type GtagFunction = (...args: [string, ...unknown[]]) => void;
  type ClarityFunction = (...args: [string, ...unknown[]]) => void;
  type FbqFunction = ((...args: unknown[]) => void) & {
    callMethod?: (...args: unknown[]) => void;
    queue: unknown[][];
    push: FbqFunction;
    loaded: boolean;
    version: string;
  };
  interface Window {
    dataLayer: unknown[];
    gtag: GtagFunction;
    clarity?: ClarityFunction;
    fbq?: FbqFunction;
    _fbq?: FbqFunction;
    google: {
      maps: GoogleMaps;
    };
  }
}

export interface AddressComponent {
  long_name: string;
  types: string[];
}
export interface ForgotPasswordEmailRequest {
  baseUrlName: string;
  userName: string;
  userType: string;
}
export interface VerifyPasswordEmailRequest {
  payload: {
    hash: string;
    password: string;
  };
}
export interface payRentResponse {
  paymentPageUrl: string;
}
export interface subscribeResponse {
  subscriptionPageUrl: string;
}
export interface ErrorType {
  message: string;
}
export interface compnayResponse {
  message: string;
  data: {
    subdomain: {
      subdomainId: number;
    };
    companySetting: {
      companySettingId: number;
      logo: string;
      primaryColor: string;
      secondaryColor: string;
      backgroundImage: string;
      backgroundColor: string;
      logoUrl: string;
      backgroundImageUrl: string;
      theme: 'light' | 'dark';
    };
  };
}
export interface buildingsSubdomainResponse {
  message: string;
  data: [
    {
      buildingId: number;
      name: string;
      buildingNumber: string;
      fullAddress: string;
    },
  ];
}
export interface unitDetailsResponse {
  message: string;
  data: [
    {
      unitId: string;
      unitNumber: string;
      unitName: string | null;
    },
  ];
}
export interface UnitRequest {
  buildingId: string;
  unitNumber: string;
  unitName: string;
}
export interface payNumberRequest {
  buildingId: string;
  unitId: string;
}
export interface payNumberResponse {
  message: string;
  data: { payNumber: string };
}

export interface ResidentExistsResponse {
  message: string;
  data: {
    exists: boolean;
  };
}

export interface PayNumberDetailsResponse {
  message: string;
  data: {
    rentAmount: number | null;
    address: {
      addressId: string | null;
      unitNumber: string | null;
      buildingNumber: string | null;
      streetName: string | null;
      city: string | null;
      province: string | null;
      provinceCode: string | null;
      country: string | null;
      countryCode: string | null;
      postalCode: string | null;
      fullAddress: string | null;
    };
  };
}
export interface subdomainResponse {
  message: string;
  data: {
    subdomainId: number;
    isValid: boolean;
    isReserved: boolean;
  };
}
export type PlacementTypes =
  | 'bottomStart'
  | 'bottomEnd'
  | 'topStart'
  | 'topEnd'
  | 'leftStart'
  | 'leftEnd'
  | 'rightStart'
  | 'rightEnd'
  | 'auto'
  | 'autoVerticalStart'
  | 'autoVerticalEnd'
  | 'autoHorizontalStart'
  | 'autoHorizontalEnd';
export interface ResetPasswordRequest {
  passwordData: {
    oldPassword: string;
    newPassword: string;
    confirmPassword: string;
  };
}
export interface ResetPasswordResponse {
  message: string;
}

export interface OpenOrderResponse {
  internalRequestId: number;
  status: string;
  errCode: number;
  reason: string;
  merchantId: string;
  merchantSiteId: string;
  version: string;
  clientRequestId: string;
  sessionToken: string;
  clientUniqueId: string;
  orderId: number;
  userTokenId: string;
  message: string;
}

export interface BankAccountNumberFixedLengths {
  values: number[];
}

export interface Bank {
  bankNameId: string;
  name: string;
  institutionNumber: string | null;
  accountNumberMinLength: number;
  accountNumberMaxLength: number;
  accountNumberFixedLengths: BankAccountNumberFixedLengths;
  transitNumberFixedValue: string | null;
  transitNumberStartsWith: string | null;
}

export interface BanksResponse {
  message: string;
  data: Bank[];
}

export interface TenantBankAccount {
  accountName: string;
  transitNumber: string;
  institutionNumber: string;
  accountNumber: string;
  bankName: string;
  voidChequePath: string;
}

export interface CreditCardToken {
  ccTempToken: string;
  sessionToken: string;
}

export interface PaymentOptionRequest {
  is3DS: boolean;
  citTransactionId?: string;
  userPaymentOptionId?: string;
  tenantBankAccount?: TenantBankAccount;
  creditCardToken?: CreditCardToken;
  isDefault?: boolean;
}

export interface PaymentOption {
  id: string;
  paymentOptionId: string;
  paymentMethodId?: string | null;
  cardCompanyTypeId?: string | null;
  cardEndingDigits: string | null;
  expiryMonth: string | null;
  expiryYear: number | null;
  cardHolderName: string | null;
  paymentMethod: string;
  cardBrand: string | null;
  paymentProcessor: string;
  address: string | null;
  city: string | null;
  provinceCode: string | null;
  postalCode: string | null;
  country: string | null;
  countryCode: string | null;
  unitNumber: string | null;
  buildingNumber: string | null;
  streetName: string;
  fixedProcessingFee: string;
  percentageRate: string;
  isDefault: boolean;
  isPremiumCard: boolean;
  cardCategoryName: string | null;
  status?: string;
  tenantBankAccountId?: string | null;
  bankAccountName?: string | null;
  bankTransitNumber?: string | null;
  bankInstitutionNumber?: string | null;
  bankAccountNumber?: string | null;
  bankName?: string | null;
  voidChequePath?: string | null;
  voidChequeUrl?: string | null;
  isBankAccountLinked?: boolean;
  fees?: PaymentOptionFee[];
  businessDaysBeforeDueDate?: number;
}

export interface SafeCharge {
  fields: (options: FieldsOptions) => FieldsInstance;
  authenticate3d: (
    options: Record<string, unknown>,
    callback: (result: Record<string, unknown>) => void
  ) => void;
  createPayment: (
    options: Record<string, unknown>,
    callback: (result: Record<string, unknown>) => void
  ) => void;
  addCardUpo: (
    options: Record<string, unknown>,
    callback: (result: Record<string, unknown>) => void
  ) => void;
  getCardDetails: (
    options: Record<string, unknown>,
    callback: (result: Record<string, unknown>) => void
  ) => void;
}

interface FieldsOptions {
  fonts: { cssUrl: string }[];
  locale: string;
  fontSize: string;
}

interface FieldsInstance {
  create: (field: string, options?: CreateOptions) => Field;
}

interface CreateOptions {
  style?: {
    base?: {
      fontFamily?: string;
      fontSize?: string;
      color?: string;
    };
  };
}

interface Field {
  attach: (selector: string) => void;
  on: (event: string, handler: (event: any) => void) => void;
  clear?: () => void;
}

export interface billingInfo {
  countryCode: string;
  fullAddress: string;
  postalCode: string;
  provinceCode: string;
  city: string;
}

export interface UpdateCardRequestBody {
  billingInfo: billingInfo;
}

export interface PaymentData {
  amountPaid: number;
  timestampAdded: string;
  rentAmount: string;
  processingFee: string;
  fixedProcessingFee: string;
  paymentDate: string;
  hasPayment: boolean;
}
export interface TransactionsStatusResponse {
  message: string;
  data: PaymentData[];
}

export interface ProcessingFee {
  fixed: string;
  percentage: string;
  total: string;
  percentageRate: string;
}

export interface PaymentSummary {
  directSummary: DirectSummary;
  subscriptionSummary: SubscriptionSummary;
  annualSaving: string;
}

export interface DirectSummary {
  processingFee: {
    fixed: string;
    percentage: string;
    total: string;
    percentageRate: string;
  };
  amount: string;
  totalAmount: string;
  discountAmount: string;
  rentAmount: string;
  promoCodeDiscountAmount?: string;
}

export interface SubscriptionSummary {
  processingFee: {
    fixed: string;
    percentage: string;
    total: string;
    percentageRate: string;
  };
  amount: string;
  totalAmount: string;
  discountAmount: string;
  rentAmount: string;
  promoCodeDiscountAmount?: string;
}

export interface Period {
  day: string;
  month: string;
  year: string;
}

export interface SubscriptionPeriod {
  startAfter: Period;
  endAfter: Period;
  recurringPeriod: Period;
  firstPaymentDate: string;
  endDate: string;
}

export interface PaymentSummaryResponse {
  message: string;
  data: PaymentSummary;
  subscriptionPeriod: SubscriptionPeriod;
}

export interface SubscriptionDetail {
  tenantMitSubscriptionId: string;
  tenantPaymentOptionId: string;
  tenantBuildingId: string;
  rentAmount: number;
  dueDate: number;
  dueDateType: string;
  paymentMethod: string;
  cardCompanyType: string;
  processingFee: {
    fixed: string;
    percentage: string;
    total: string;
    percentageRate: string;
  };
  amount: number;
  totalAmount: number;
  timestampAdded: string;
}

export interface SubscriptionDetailResponse {
  message: string;
  data: SubscriptionDetail;
}
export interface InitiateMFAResponse {
  message: string;
}
export interface mfaResponse {
  message: string;
  data: {
    loginToken: string;
  };
}
export interface mfaCookieResponse {
  message: string;
  data: {
    isMfaValid: true;
    isMfaEnabledForUser: true;
    isMfaEnabledGlobally: true;
    isMfaRequired: false;
  };
}
export interface termsResponse {
  message: string;
  data: {
    termsAndConditionId: number;
    value: string;
  };
}
export interface termsRequest {
  termsAndConditionId: string;
}
export interface checkConsentResponse {
  message: string;
  data: {
    isUserConsentRequired: boolean;
    timestampAdded: string;
  };
}

export interface TenantProcessingFee {
  key: string;
  tenantProcessingFeeId: string;
  paymentMethodId: string;
  cardCompanyTypeId: string;
  paymentType: string;
  percentageRate: string;
  fixedProcessingFee?: string;
  paymentMethodName: string;
  cardCompanyTypeValue: string;
  cardCategoryId: string;
  cardCategoryName: string;
}

export interface processingFeeDetailsResponse {
  message: string;
  data: TenantProcessingFee[];
}
export interface MfaItem {
  mfaId: string;
  mfaTypeId: string;
  name: 'phoneNumber' | 'email';
  isEnabled: boolean;
}
export interface mfaListResponse {
  message: string;
  data: MfaItem[];
}
export interface removeMFAResponse {
  message: string;
}
export interface addMFAResponse {
  message: string;
  data: {
    loginToken: string;
    mfaToken: string;
  };
}

export interface initiateCryptoSessionResponse {
  tenantPaymentSessionId: string;
  userId: string;
  tenantBuildingId: string;
  tenantPaymentProcessorId: string;
  rentAmount: number;
  fixedFee: number;
  percentageFee: number;
  percentageRate: number;
  amountPaid: number;
  processingFee: number;
  removed: number;
  timestampAdded: string;
  timestampUpdated: string;
}

export interface markIDVApplicationResponse {
  message: string;
}

export interface Notification {
  notificationId: string;
  message: string;
  timestampViewed: string;
  timestampAdded: string;
  notificationTypeName: NotificationTypes;
  referenceId: string;
}

export interface PaginationMeta {
  page: number;
  limit: number;
  totalCount: number;
  lastPage: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}

export interface NotificationResponse {
  meta: PaginationMeta;
  data: Notification[];
}
export interface ActiveBuilding {
  tenantBuildingId: string;
  name: string;
  rentAmount: number;
  addressId: string;
  fullAddress: string;
}
export interface PaginationMeta {
  page: number;
  limit: number;
  totalCount: number;
  lastPage: number;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
}
export interface activeBuildingsList {
  data: EquifaxEligibleBuilding[];
  meta: PaginationMeta;
}
export interface activateCreditBuilderResponse {
  message: string;
}
export interface activateCreditBuilderBody {
  surname: string;
  firstName: string;
  middleName: string;
  dateBirth: string;
  telephoneNumber: string;
  address: {
    fullAddress: string;
    streetName: string;
    city: string;
    province: string;
    provinceCode: string;
    country: string;
    countryCode: string;
    postalCode: string;
    buildingNumber: string;
    unitNumber: string;
  };
}
export interface UnsubscribeMail {
  message: string;
}
export interface CreditBuilderDetails {
  equifaxUserDataId: string;
  userId: string;
  tenantId: string;
  surname: string;
  firstName: string;
  middleName?: string;
  dateBirth: string;
  telephoneNumber: string;
  isSubscribed: number;
  subscribedOn: string;
  unsubscribedOn: string | null;
  tenantBuildingId: string;
  addressId: string;
  streetName: string;
  buildingNumber: string | null;
  city: string;
  provinceCode: string;
  postalCode: string;
}
export interface tenantProgressRespone {
  message: string;
  data: {
    steps: {
      accountCreated: boolean;
      isEmailVerified: boolean;
      addedBuilding: boolean;
      completeBuilding: boolean;
      isIdentityVerified: boolean;
      addedBuildingAddress: boolean;
    };
    percentage: number;
  };
}
export interface UserPointsSummary {
  userPointId: string;
  totalPoints: string;
  totalAvailable: string;
  totalRedeemed: string;
  expiryDate?: string | null;
}

export interface UserTicketsSummary {
  totalTickets: string;
  totalAvailable: string;
  totalRedeemed: string;
}

export interface userPointsResponse {
  message: string;
  data: {
    points: UserPointsSummary;
    tickets: UserTicketsSummary;
  };
}

export type PointTaskCode =
  | 'tenantSignup'
  | 'firstPayment'
  | 'rentPayment'
  | 'secondBuildingRentPayment'
  | 'oneWeekAdvance'
  | 'twoWeekAdvance'
  | 'threeWeekAdvance'
  | 'subscriptionPayment'
  | 'frequentUser'
  | 'answerSurvey'
  | 'rentPayment3MonthStreak'
  | 'rentPayment6MonthStreak'
  | 'rentPayment12MonthStreak'
  | 'completeBuildingDetails'
  | 'thirdPayment'
  | 'firstSubscriptionPayment'
  | 'thirdSubscriptionPayment'
  | 'inAppReview'
  | 'appReview'
  | 'viewCreditScore'
  | 'firstPaymentWithRentReporting'
  | 'paymentWithRentReporting'
  | 'newsletterSignup'
  | 'referralReward'
  | 'referralBonusTier1'
  | 'referralBonusTier2'
  | 'referralBonusTier3'
  | 'socialShoutOut'
  | 'socialFollow'
  | 'anniversaryReward'
  | 'birthdayBonus';

export type PointCategoryName =
  | 'New User Welcome Bonuses'
  | 'Financial Health & Punctuality'
  | 'Deep Engagement & Stickiness'
  | 'Community & Social Growth'
  | 'Milestone & Seasonal Events';

export type PointTagType = 'Lifetime' | 'Repeatable' | 'Streak';

export interface OfferProgress {
  completedCount: number;
  totalCount: number;
}

export interface Offer {
  pointTaskId: string;
  code: PointTaskCode;
  description: string;
  maxCountPerUser: number | null;
  hasProgress: boolean;
  displayLimit: string | null;
  showOnlyStarted: boolean;
  isDynamic: boolean;
  bestPaymentMethod: string | null;
  threshold: number | null;
  pointCategoryId: string | null;
  pointCategoryName: PointCategoryName | null;
  minTickets: number;
  maxTickets: number;
  minPoints: number;
  maxPoints: number;
  completedCount: number;
  lastCompletedAt: string | null;
  tags: PointTagType[];
  isCompleted: boolean;
  progress: OfferProgress | null;
}

export interface OfferCategoryStats {
  pointCategoryId: string;
  pointCategoryName: PointCategoryName;
  totalTasks: number;
  totalCompleted: number;
}

export interface OfferStats {
  totalTasks: number;
  totalCompleted: number;
  categories: OfferCategoryStats[];
}

export interface PointTasksStatsCategory extends OfferCategoryStats {
  totalMinPoints: number;
  totalMaxPoints: number;
  totalMinTickets: number;
  totalMaxTickets: number;
}

export interface PointTasksStats extends Omit<OfferStats, 'categories'> {
  categories: PointTasksStatsCategory[];
}

export interface GetOffersResponse {
  tasks: Offer[];
  stats: OfferStats;
}
export interface sendReferralCodeInterface {
  message: string;
}

export interface RewardsHistoryItem {
  pointTransactionId: string;
  userId: string;
  pointTaskId: string;
  transactionType: TransactionType;
  amount: string;
  assetType: 'points' | 'tickets';
  timestampAdded: string;
  pointRewardTypeId: string;
  pointRewardTypeCode: string;
  pointRewardTypeDescription: string;
  pointTaskCode: string;
  pointTaskDescription: string;
}

export interface RewardsHistoryResponse {
  message: string;
  data: RewardsHistoryItem[];
  meta: PaginationMeta;
}

export interface GetGiftBitBrandsResponse {
  data: GiftBitBrand[];
  meta: PaginationMeta;
}

export interface GiftBitBrand {
  brand_code: string;
  name: string;
  image_url: string;
  disclaimer: string;
}
export interface GiftCardsDetailsResponse {
  brand: {
    brand_code: string;
    name: string;
    image_url: string;
    disclaimer: string;
    description: string;
    regions: number[];
    fund_currencyisocode: string;
    embeddable: boolean;
    variable_price: boolean;
    min_price_in_cents: number;
    max_price_in_cents: number;
    allowed_prices_in_cents: number[];
  };
  info: {
    code: string;
    name: string;
    message: string;
  };
}
interface ConvertPointsData {
  points: string[];
}
export interface convertAmountPoints {
  message: string;
  data: ConvertPointsData;
}
export interface sendGiftCardResponse {
  message: string;
}

export interface referralListResponse {
  message: string;
  data: Array<{
    referralId: string;
    email: string;
    invitedOn: string;
    isInvited: boolean;
    isQualified: boolean;
    isSignedUp: boolean;
    qualifiedOn: string | null;
    referralUserId: string | null;
    removed: boolean;
    signedUpOn: string | null;
    timestampAdded: string;
    timestampRemoved: string | null;
    userId: string;
  }>;
  meta: PaginationMeta;
}
export interface RentCreditOption {
  rentCreditOptionId: string;
  amount: number;
  points: string;
  removed: boolean;
  timestampAdded: string;
  timestampUpdated: string;
  timestampRemoved: string;
}
export interface rentCreditResponse {
  totalRentCredit: number;
  totalAvailable: number;
  totalRedeemed: number;
}
export interface redeemRentCreditResponse {
  message: string;
  pointTransactionId: string;
  rentCreditOptionId: string;
  amount: number;
  points: string;
}

export type EquifaxCreditHealthUrlResponse = {
  message: string;
  data: {
    url: string;
  };
};

export interface FAQResponse {
  faqId: string;
  category: string;
  question: string;
  answer: string;
}

export interface TimelineStep {
  step: string;
  label: string;
  timestamp: string | null;
}

export interface TransactionTimeline {
  paymentSuccess: string;
  paymentSendToLandlord: string;
  batchCreatedAt: string;
  paymentReceivedByLandlord: string;
  paymentReportedToEquifax: string;
  refundDate: string;
  timelineSteps?: TimelineStep[];
}

export interface TransactionTimelineResponse {
  message: string;
  data: TransactionTimeline;
}

export interface UpdateDueDateTypeRequest {
  buildingDetails: {
    dueDateTypeName: string;
    dueDate: number;
  };
}

export interface WalletHistoryItem {
  rentCreditTransactionId: string;
  rentCreditTransactionType: string;
  amount: number;
  note: string;
  isReverted: number;
  timestampAdded: string;
  timestampReverted: string | null;
  tenantTransactionId: string | null;
  cardLast4Digits: string | null;
  tenantBuildingId: string;
  paymentMethodId: string | null;
  paymentMethodName: string | null;
  cardCompanyTypeId: string | null;
  cardCompanyTypeValue: string | null;
}

export interface WalletHistoryResponse {
  message: string;
  data: WalletHistoryItem[];
  meta: PaginationMeta;
}

export interface DynamicOffer {
  pointTaskId: string;
  code: string;
  dynamicPoints: string | number;
  tickets?: string;
}

export interface GetDynamicOffersResponse {
  message: string;
  data: DynamicOffer[];
}

export interface PreviewPaymentRewardsRequestPayment {
  paymentMethodId: string;
  cardCompanyTypeId?: string;
  paymentType: 'direct' | 'subscription';
  amount?: number;
  dueDateType?: string;
  tenantBuildingId?: string;
}

export interface PreviewPaymentRewardsRequest {
  payments: PreviewPaymentRewardsRequestPayment[];
}

export interface PreviewPaymentRewardsTask {
  code: string;
  points: number;
  tickets: number;
  isEligible: boolean;
}

export interface PreviewPaymentRewardsItem {
  totalPoints: number;
  totalTickets: number;
  tasks?: PreviewPaymentRewardsTask[];
}

export interface PreviewPaymentRewardsResponse {
  message?: string;
  data: PreviewPaymentRewardsItem[];
}

export interface ReferralStats {
  totalInvited: number;
  totalPoints: number;
  totalUninvited: number;
  totalSignedUp: number;
  qualifiedReferrals: number;
}
export enum PaymentMethodType {
  CreditCard = 'Credit Card',
  DebitCard = 'Debit Card',
  GooglePay = 'Google Pay',
  ApplePay = 'Apple Pay',
  PayPal = 'PayPal',
  BillPayment = 'Bill Payment',
  CreditDebitCard = 'Credit/Debit Card',
  InteracETransfer = 'Interac e-Transfer',
  Cryptocurrency = 'Cryptocurrency',
  PreAuthorizedDebit = 'preAuthorizedDebit',
}

export const PAD_VERIFICATION_LABELS = {
  plaid: 'Verified via Plaid',
  manual: 'Manually verified',
} as const;

export const getPadVerificationLabel = (isBankAccountLinked?: boolean): string =>
  isBankAccountLinked ? PAD_VERIFICATION_LABELS.plaid : PAD_VERIFICATION_LABELS.manual;

export enum TransactionType {
  Credit = 'credit',
  Debit = 'debit',
}

export enum NotificationTypes {
  PaymentSuccess = 'paymentSuccess',
  PaymentFailed = 'paymentFailed',
  SubscriptionSuccess = 'subscriptionSuccess',
  SubscriptionFailed = 'subscriptionFailed',
  SubscriptionPaymentSuccess = 'subscriptionPaymentSuccess',
  SubscriptionPaymentFailed = 'subscriptionPaymentFailed',
  PointEarned = 'pointEarned',
  MitSubscriptionSuccess = 'mitSubscriptionSuccess',
  MitSubscriptionFailed = 'mitSubscriptionFailed',
  MitPaymentSuccess = 'mitPaymentSuccess',
  MitPaymentFailed = 'mitPaymentFailed',
  TenantIdvSubmitted = 'tenantIdvSubmitted',
  TenantIdvChangeRequest = 'tenantIdvChangeRequest',
  TenantProfileIncomplete = 'tenantProfileIncomplete',
}

export interface EquifaxEligibleBuilding {
  tenantBuildingId: string;
  name: string;
  rentAmount: number;
  fullAddress: string;
  streetName: string;
  province: string;
  city: string;
  provinceCode: string | null;
  postalCode: string;
  countryCode: string;
  leaseDocumentUrl: string | null;
  country: string;
  leaseStartDate: string | null;
  leaseEndDate: string | null;
  leaseDocumentDownloadUrl: string | null;
  buildingNumber: string | null;
  unitNumber?: string | null;
}
export interface EquifaxSubscribedBuilding {
  tenantBuildingId: string;
  name: string;
  rentAmount: number;
  timestampLeaseUploaded: string;
}
export interface EquifaxSubscribedBuildingResponse {
  data: Array<EquifaxSubscribedBuilding>;
  meta: PaginationMeta;
}

export interface PaymentOptionFee {
  tenantProcessingFeeId: string;
  percentageRate: string;
  fixedProcessingFee: string;
  paymentType: string;
}

export interface CreatePaymentOptionResponse {
  id: string;
  paymentOptionId: string;
  isDefault: number;
  cardEndingDigits: string;
  expiryMonth: string;
  expiryYear: number;
  cardBrandType: string | null;
  cardHolderName: string;
  paymentMethodId: string;
  paymentMethod: string;
  cardCompanyTypeId: string;
  cardBrand: string;
  cardCategoryId: string;
  cardCategoryName: string;
  paymentProcessor: string;
  address: string;
  city: string;
  provinceCode: string | null;
  postalCode: string;
  country: string;
  countryCode: string;
  isPremiumCard: boolean;
  tenantProcessingFeeId: string;
  percentageRate: string;
  fixedProcessingFee: string;
  fees?: PaymentOptionFee[];
}
export interface GroupedPaymentMethod {
  method: string; // e.g., "Credit Card", "Debit"
  companies: CompanyGroup[]; // list of card companies under this method
}

export interface CompanyGroup {
  name: string; // e.g., "Visa", "Mastercard", "Other"
  categories: FeeCategory[]; // card categories under this company
}

export interface FeeCategory {
  id: string; // tenantProcessingFeeId
  name: string; // e.g., "Infinite", "Business"
  directRate?: string; // e.g., "2.75%"
  subscriptionRate?: string; // e.g., "3.25%"
}

export interface Stakeholder {
  tenantStakeholderId: string;
  firstName: string;
  lastName: string;
  email: string;
}

export interface StakeholderResponse {
  message: string;
  data: Stakeholder[];
}

export interface TenantCompany {
  tenantCompanyId: string;
  name: string;
  displayName: string;
  documentPath: string;
  kycBusinessTypeId: string;
  kycBusinessTypeValue: string;
  documentUrl: string;
}

// -----------------------------
// API responses used by requests
// -----------------------------

export interface PadTermsResponse {
  padAuthorizationTermId: string;
  value: string;
}

export type GetResponse<T> = {
  message: string;
  data: T;
};

export type PlaidLinkResponse = GetResponse<{
  link: string;
}>;

export type PlaidCallbackResponse = GetResponse<{
  count: number;
}>;

export type PointTasksStatsApiResponse = {
  message: string;
  data: {
    totalTasks: number;
    totalCompleted: number;
    categories: Array<{
      pointCategoryId: string;
      pointCategoryName: PointCategoryName;
      totalTasks: number;
      totalCompleted: number;
      totalMinPoints: number;
      totalMaxPoints: number;
      totalMinTickets: number;
      totalMaxTickets: number;
    }>;
  };
};

export type ContestStatsResponse = {
  message: string;
  data: {
    contestWinnersCount: number;
    rentCreditAwardedUsersCount: number;
    ticketAwardedUsersCount: number;
  };
};

export type RemainingTimeFromNow = {
  days: number;
  hours: number;
  minutes: number;
  seconds: number;
};

export type ContestTimeResponse = {
  message: string;
  data: {
    startTime: string;
    endTime: string;
    remainingStartTimeFromNow: RemainingTimeFromNow;
    remainingEndTimeFromNow: RemainingTimeFromNow;
  };
};

export type ContestLeaderboardItem = {
  contestWinnerId: string;
  userId?: string;
  firstName: string;
  prizeId: string;
  prizeName: string;
  prizeAmount: string;
  prizeQuantity: number;
  prizeTypeId: string;
  prizeTypeName: string;
  awardRank?: number;
  address?: string;
};

export type ContestPrizeItem = {
  prizeId: string;
  prizeTypeId: string;
  prizeTypeName: string;
  awardRank: number;
  amount: string;
  quantity: number;
  displayName: string;
};

export type ContestPrizeListResponse = {
  message: string;
  data: ContestPrizeItem[];
};

export type ContestLeaderboardResponse = {
  message: string;
  meta: PaginationMeta;
  data: ContestLeaderboardItem[];
};

export enum ContestWinnerStatusEnum {
  Pending = 'PENDING',
  Awarded = 'AWARDED',
  Expired = 'EXPIRED',
  Forfeited = 'FORFEITED',
  Cancelled = 'CANCELLED',
}

export enum PrizeTypeEnum {
  Ticket = 'ticket',
  RentCredit = 'rentCredit',
}

/** GET /api/v2/contests/status — optional fields only present when hasWon is true. */
export type ContestWinnerStatusData = {
  hasWon: boolean;
  prizeTypeName?: PrizeTypeEnum;
  prizeName?: string;
  status?: ContestWinnerStatusEnum;
};

export type ContestWinnerStatusResponse = GetResponse<ContestWinnerStatusData>;

export type SkillTestData = {
  skillTestId: string;
  question: string;
  openedAt: string | null;
  answerDeadline: string | null;
  attemptCount: number;
  status: ContestWinnerStatusEnum;
};

export type SkillTestResponse = GetResponse<SkillTestData>;

export type SocialSubmissionEventType = 'Social Shout Out' | 'Social Follow';
export type SocialMediaPlatform = 'Instagram' | 'TikTok';
export type SocialSubmissionStatus =
  | 'Pending'
  | 'Submitted'
  | 'Approved'
  | 'Decline'
  | 'Change Request';

export interface SocialSubmission {
  socialSubmissionId: string;
  eventType: SocialSubmissionEventType;
  socialMediaType: { name: SocialMediaPlatform };
  username: string;
  postUrl: string | null;
  status: SocialSubmissionStatus;
  reviewerComment: string | null;
  timestampSubmitted: string | null;
  timestampReviewed: string | null;
  timestampAdded: string;
}

export interface SocialSubmissionListResponse {
  message: string;
  data: SocialSubmission[];
  meta: PaginationMeta;
}

export interface CreateSocialSubmissionRequest {
  eventType: SocialSubmissionEventType;
  platform: SocialMediaPlatform;
  username: string;
  postUrl?: string;
}

export type UpdateSocialSubmissionRequest = Pick<CreateSocialSubmissionRequest, 'username' | 'postUrl'>;

export type SkillTestMessageResponse = {
  message: string;
};
